import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ShieldCheck,
  Lock,
  Eye,
  Database,
  Mail,
  Cookie,
  UserCheck,
} from "lucide-react";
import { fadeUp } from "@/lib/motion";
import SEO from "@/components/common/SEO";
import { getPageSeo } from "@/lib/seo";

const Section = ({ icon: Icon, title, children, testId }) => (
  <motion.section
    {...fadeUp}
    data-testid={testId}
    className="glass-card p-7 md:p-9"
  >
    <div className="flex items-start gap-4">
      <span className="flex h-11 w-11 items-center justify-center rounded-full border border-[color:rgba(212,175,55,0.4)] bg-black/30 flex-shrink-0">
        <Icon className="h-5 w-5 text-[color:var(--sp-gold)]" />
      </span>
      <div className="flex-1">
        <h2 className="font-display text-2xl md:text-3xl text-white leading-tight">
          {title}
        </h2>
        <div className="mt-4 space-y-4 text-[15px] leading-relaxed text-white/75">
          {children}
        </div>
      </div>
    </div>
  </motion.section>
);

const PrivacyPage = () => {
  const lastUpdated = "January 2026";
  const seo = getPageSeo("privacy");
  return (
    <>
      <SEO
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        robots={seo.robots}
      />
      <section
        data-testid="privacy-hero"
        className="relative pt-40 pb-12"
      >
        <div className="absolute inset-0 hero-overlay opacity-80" />
        <div className="container-narrow relative">
          <Link
            to="/"
            data-testid="privacy-back-home"
            className="inline-flex items-center gap-2 text-[11px] tracking-[0.32em] uppercase text-white/65 hover:text-[color:var(--sp-gold)] transition-colors mb-6"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back Home
          </Link>
          <motion.p
            {...fadeUp}
            className="text-[11px] tracking-[0.36em] uppercase text-[color:var(--sp-gold)]"
          >
            <span className="gold-divider" /> Legal · Privacy
          </motion.p>
          <motion.h1
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.1 }}
            className="font-display text-5xl md:text-6xl lg:text-7xl mt-5 leading-[1.05] max-w-4xl"
          >
            Privacy & <span className="italic text-white/85">Security</span>.
          </motion.h1>
          <motion.p
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.2 }}
            className="mt-6 max-w-2xl text-white/70 text-base leading-relaxed"
          >
            Your trust is our most important asset. This Privacy Policy explains
            what we collect, how we use it, and the controls you have over your
            personal data.
          </motion.p>
          <p className="mt-4 text-xs tracking-[0.24em] uppercase text-white/45">
            Last updated · {lastUpdated}
          </p>
        </div>
      </section>

      <section data-testid="privacy-content" className="pb-24">
        <div className="container-narrow space-y-6">
          <Section
            icon={Database}
            title="1. Information We Collect"
            testId="privacy-section-collect"
          >
            <p>
              When you interact with Seetai Properties through this website,
              certain forms, or WhatsApp, we may collect:
            </p>
            <ul className="list-disc list-outside ml-5 space-y-2 text-white/70">
              <li>
                <strong>Contact details</strong> — your full name, phone number,
                email address, and project of interest (provided voluntarily via
                contact / brochure forms).
              </li>
              <li>
                <strong>Communications</strong> — messages or enquiries you send
                us through forms, email, WhatsApp, or phone.
              </li>
              <li>
                <strong>Technical data</strong> — limited browser metadata such
                as IP address, device type, and pages visited, captured by
                standard server logs.
              </li>
              <li>
                <strong>Cookies</strong> — only essential session cookies needed
                for the website to operate. We do not currently use advertising
                or third-party tracking cookies.
              </li>
            </ul>
          </Section>

          <Section
            icon={Eye}
            title="2. How We Use Your Information"
            testId="privacy-section-use"
          >
            <p>We use the information you provide to:</p>
            <ul className="list-disc list-outside ml-5 space-y-2 text-white/70">
              <li>
                Respond to enquiries, schedule site visits, and share project
                materials such as brochures.
              </li>
              <li>
                Maintain a record of leads in our internal CRM so our advisory
                team can follow up with you.
              </li>
              <li>
                Improve our website, content, and customer experience over
                time.
              </li>
              <li>
                Send legitimate, business-relevant updates regarding the
                project(s) you expressed interest in. You can opt out anytime.
              </li>
            </ul>
            <p>
              We <strong>do not</strong> sell, rent, or trade your personal data
              to any third party for advertising or marketing purposes.
            </p>
          </Section>

          <Section
            icon={Lock}
            title="3. Data Security"
            testId="privacy-section-security"
          >
            <p>
              We take reasonable, industry-standard measures to safeguard your
              personal data:
            </p>
            <ul className="list-disc list-outside ml-5 space-y-2 text-white/70">
              <li>
                All data is transmitted over <strong>encrypted HTTPS</strong>{" "}
                channels.
              </li>
              <li>
                Lead and CRM records are stored in <strong>access-controlled</strong> databases with restricted
                admin authentication.
              </li>
              <li>
                Passwords for administrative accounts are hashed using strong,
                one-way algorithms.
              </li>
              <li>
                Regular reviews are performed on infrastructure, dependencies,
                and access logs.
              </li>
            </ul>
            <p>
              While we make every reasonable effort, no system on the public
              internet can be guaranteed 100% secure. We urge you to use strong
              passwords and report any suspicious activity to us at{" "}
              <a
                href="mailto:info@seetaiproperties.com"
                className="text-[color:var(--sp-gold)] hover:underline"
              >
                info@seetaiproperties.com
              </a>
              .
            </p>
          </Section>

          <Section
            icon={Cookie}
            title="4. Cookies & Tracking"
            testId="privacy-section-cookies"
          >
            <p>
              The website uses only essential session storage to remember
              admin authentication tokens (for logged-in admin users) and
              minimal preferences. We do not deploy third-party advertising,
              re-marketing, or social pixel trackers on this website.
            </p>
            <p>
              You can configure your browser to refuse or alert you about
              cookies at any time. Disabling them may, however, affect basic
              site functionality.
            </p>
          </Section>

          <Section
            icon={UserCheck}
            title="5. Your Rights & Choices"
            testId="privacy-section-rights"
          >
            <p>
              You have the right to:
            </p>
            <ul className="list-disc list-outside ml-5 space-y-2 text-white/70">
              <li>
                <strong>Access</strong> — request a copy of the personal data
                we hold about you.
              </li>
              <li>
                <strong>Correction</strong> — ask us to update inaccurate or
                incomplete data.
              </li>
              <li>
                <strong>Deletion</strong> — request removal of your data from
                our CRM (subject to legal record-keeping obligations).
              </li>
              <li>
                <strong>Opt-out</strong> — unsubscribe from any non-essential
                marketing communication at any time.
              </li>
            </ul>
            <p>
              To exercise any of these rights, write to us at{" "}
              <a
                href="mailto:info@seetaiproperties.com"
                className="text-[color:var(--sp-gold)] hover:underline"
              >
                info@seetaiproperties.com
              </a>
              .
            </p>
          </Section>

          <Section
            icon={ShieldCheck}
            title="6. Third-Party Services"
            testId="privacy-section-third"
          >
            <p>
              We integrate with a small number of essential third-party
              services to operate this website:
            </p>
            <ul className="list-disc list-outside ml-5 space-y-2 text-white/70">
              <li>
                <strong>WhatsApp</strong> — when you tap “Chat on WhatsApp”,
                you’re handed off to Meta’s WhatsApp service, which has its own
                privacy policy.
              </li>
              <li>
                <strong>Google Maps</strong> — used to embed the project
                location map. Subject to Google’s privacy policy.
              </li>
              <li>
                <strong>Email service providers</strong> — used to deliver
                transactional messages.
              </li>
            </ul>
            <p>
              We share with these providers only the minimum data necessary to
              deliver their function.
            </p>
          </Section>

          <Section
            icon={Mail}
            title="7. Children's Privacy"
            testId="privacy-section-children"
          >
            <p>
              This website is intended for an adult audience evaluating
              residential real estate. We do not knowingly collect personal data
              from children under the age of 18. If you believe a minor has
              submitted information to us, please contact us so we can delete
              it.
            </p>
          </Section>

          <Section
            icon={ShieldCheck}
            title="8. Changes to This Policy"
            testId="privacy-section-changes"
          >
            <p>
              We may update this Privacy Policy from time to time to reflect
              changes in technology, regulation, or our business practices. The
              “Last updated” date at the top of this page reflects the most
              recent revision. We encourage you to review this page
              periodically.
            </p>
          </Section>

          <Section icon={Mail} title="9. Contact Us" testId="privacy-section-contact">
            <p>
              For any privacy-related question, request, or grievance, write
              to:
            </p>
            <p className="text-white">
              <strong>Privacy Officer</strong>
              <br />
              Seetai Properties Private Limited
              <br />
              Pune & Pandharpur, Maharashtra, India
              <br />
              Email:{" "}
              <a
                href="mailto:info@seetaiproperties.com"
                className="text-[color:var(--sp-gold)] hover:underline"
              >
                info@seetaiproperties.com
              </a>
              <br />
              Phone: +91 96372 41414
            </p>
          </Section>
        </div>
      </section>
    </>
  );
};

export default PrivacyPage;
